import Checkbox from 'components/Checkbox/checkbox'
import { cn } from 'components/UI/cn'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, Search, XCircle } from 'lucide-react'
import { PiCaretDown, PiMagnifyingGlass, PiX } from 'react-icons/pi'
import { getDropdownZIndex } from 'styles/z-index'

/**
 * Hook to dismiss a dropdown on click outside or Escape key.
 * Attaches document listeners only while the dropdown is open.
 */
export function useDropdownDismiss(
  containerRef: React.RefObject<HTMLElement | null>,
  open: boolean,
  onClose: () => void
): void {
  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open, containerRef, onClose])

  useEffect(() => {
    if (!open) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, onClose])
}

export interface SectionedItem<T = string> {
  label: string
  value: T
}

export interface SectionedSection<T = string> {
  key: string
  items: SectionedItem<T>[]
}

export interface SectionedMultiSelectProps<T = string> {
  /** Label shown before the colon on the trigger button. */
  buttonLabel: string
  /** Sections of checkbox items separated by dividers. */
  sections: SectionedSection<T>[]
  /** Currently selected values across all sections. */
  selected: T[]
  /** Called with new selection when user clicks Apply. */
  onApply: (selected: T[]) => void
  /** Placeholder text for the search input. */
  searchPlaceholder?: string
  /** Text shown on the trigger when nothing is selected. */
  emptyLabel?: string
  /** Format the button display text from the current selection. */
  formatLabel?: (selected: T[], sections: SectionedSection<T>[]) => string
  /** Whether to show the search input. Defaults to true. */
  searchable?: boolean
  /** Width class for the dropdown panel. Defaults to "w-72". */
  dropdownWidth?: string
  /** When true, the trigger button always renders the empty/idle state
   *  (button label + chevron) regardless of whether anything is selected.
   *  Use when the surrounding page surfaces the selection elsewhere (e.g.
   *  as filter chips below the toolbar). */
  hideSelectionInTrigger?: boolean
  /** Which side of the trigger the dropdown menu aligns to. Defaults to
   *  'left'. Set to 'right' for triggers near the right edge of the
   *  viewport so the menu doesn't get clipped. */
  align?: 'left' | 'right'
  /** Opt into the DS2026 table-filter look for the popover: rounded-xl white
   *  card with hairline neutral border + DS2026 shadow, neutral-100 row hover,
   *  Phosphor search icon, and a bright-green Apply button. Defaults to the
   *  legacy look so other consumers are untouched. */
  ds2026?: boolean
}

/**
 * Multi-select dropdown with sectioned checkbox groups, search, and Apply button.
 *
 * Sections are separated by dividers. Selections are staged locally
 * and only committed when the user clicks "Apply".
 */
export function SectionedMultiSelect<T = string>({
  buttonLabel,
  sections,
  selected,
  onApply,
  searchPlaceholder = 'Search...',
  emptyLabel = 'All',
  formatLabel,
  searchable = true,
  dropdownWidth = 'w-72',
  hideSelectionInTrigger = false,
  align = 'left',
  ds2026 = false,
}: SectionedMultiSelectProps<T>) {
  const isActive = !hideSelectionInTrigger && selected.length > 0
  const containerRef = useRef<HTMLDivElement>(null)
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [localSelected, setLocalSelected] = useState<T[]>(selected)

  const allValues = useMemo(
    () => sections.flatMap((s) => s.items.map((i) => i.value)),
    [sections]
  )

  // Sync local state when dropdown opens
  useEffect(() => {
    if (open) {
      setLocalSelected(selected)
      setSearch('')
    }
  }, [open, selected])

  const closeDropdown = useCallback(() => setOpen(false), [])
  useDropdownDismiss(containerRef, open, closeDropdown)

  const toggleValue = useCallback((value: T) => {
    setLocalSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    )
  }, [])

  const handleSelectAll = useCallback(() => {
    setLocalSelected(allValues)
  }, [allValues])

  const handleSelectNone = useCallback(() => {
    setLocalSelected([])
  }, [])

  // DS2026 "Select all" header state: tri-state checkbox + Reset link.
  const allSelected =
    allValues.length > 0 && localSelected.length === allValues.length
  const someSelected =
    localSelected.length > 0 && localSelected.length < allValues.length
  const toggleAll = useCallback(() => {
    setLocalSelected((prev) =>
      prev.length === allValues.length ? [] : allValues
    )
  }, [allValues])

  const handleApply = useCallback(() => {
    onApply(localSelected)
    setOpen(false)
  }, [localSelected, onApply])

  /** Filter items within each section by search query. */
  const filteredSections = useMemo(() => {
    if (!search.trim()) return sections
    const q = search.toLowerCase()
    return sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          String(item.label).toLowerCase().includes(q)
        ),
      }))
      .filter((section) => section.items.length > 0)
  }, [sections, search])

  const displayLabel = formatLabel
    ? formatLabel(selected, sections)
    : selected.length > 0
      ? `${selected.length} selected`
      : emptyLabel

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onApply([])
    },
    [onApply]
  )

  return (
    <div ref={containerRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'inline-flex items-center gap-1 h-8 pl-2.5 pr-3 rounded-md text-sm whitespace-nowrap',
          ds2026
            ? // DS2026 small secondary button: white, gray-300 border, gray hover.
              cn(
                'border border-ds2026-btn-outline-stroke bg-ds2026-btn-outline-bg font-medium text-ds2026-btn-outline-text transition-colors hover:bg-ds2026-btn-outline-hover-bg hover:text-ds2026-btn-outline-hover-text',
                open &&
                  'bg-ds2026-btn-outline-hover-bg text-ds2026-btn-outline-hover-text'
              )
            : isActive
              ? 'bg-surface-active text-brand-active'
              : 'border border-line bg-surface font-medium text-gray-700 transition-shadow hover:shadow-[0px_1px_4px_rgba(12,12,13,0.08)]'
        )}
      >
        {isActive ? (
          <>
            <span>
              {buttonLabel}:{' '}
              <span className="font-semibold">{displayLabel}</span>
            </span>
            {ds2026 ? (
              <PiX
                className="size-4 flex-shrink-0 cursor-pointer"
                onClick={handleClear}
              />
            ) : (
              <XCircle
                className="size-4 text-brand-active flex-shrink-0 cursor-pointer"
                onClick={handleClear}
              />
            )}
          </>
        ) : (
          <>
            <span>{emptyLabel}</span>
            {ds2026 ? (
              <PiCaretDown className="size-4 flex-shrink-0" />
            ) : (
              <ChevronDown className="size-3.5 flex-shrink-0" />
            )}
          </>
        )}
      </button>

      {open && (
        <div
          className={cn(
            `absolute top-full ${align === 'right' ? 'right-0' : 'left-0'} mt-1`,
            ds2026
              ? // Hug content: width fits the widest row (capped), instead of a
                // fixed wide panel that looks oversized for short option lists.
                'min-w-[200px] w-max max-w-[320px] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.10),0px_2px_4px_-2px_rgba(16,24,40,0.06)]'
              : cn(
                  dropdownWidth,
                  'rounded-md border border-line bg-surface shadow-md'
                )
          )}
          style={{ zIndex }}
        >
          {/* DS2026 "Select all" header: tri-state checkbox + Reset link.
              Skipped for tiny filters (<=2 options) where select-all/none is
              redundant with just toggling the checkboxes directly. */}
          {ds2026 && allValues.length > 2 && (
            <div className="px-3 pt-3 pb-2">
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 cursor-pointer min-w-0">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    wrapperClassName="flex-shrink-0"
                  />
                  <span className="text-sm font-semibold text-neutral-800 truncate">
                    Select all
                  </span>
                </label>
                <button
                  type="button"
                  onClick={handleSelectNone}
                  disabled={localSelected.length === 0}
                  className={cn(
                    'text-sm font-medium transition-colors',
                    localSelected.length === 0
                      ? 'text-neutral-400 cursor-not-allowed'
                      : 'text-brand-active hover:text-brand-bright'
                  )}
                >
                  Reset
                </button>
              </div>
            </div>
          )}

          {/* Search input */}
          {searchable && (
            <div
              className={cn(
                'flex items-center gap-2 px-3 py-2 border-b',
                ds2026 ? 'border-neutral-200' : 'border-line'
              )}
            >
              {ds2026 ? (
                <PiMagnifyingGlass className="size-4 text-neutral-400 flex-shrink-0" />
              ) : (
                <Search className="size-4 text-fg-muted flex-shrink-0" />
              )}
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className={cn(
                  'flex-1 text-sm outline-none bg-transparent',
                  ds2026
                    ? 'text-neutral-800 placeholder:text-neutral-400'
                    : 'placeholder:text-fg-muted'
                )}
                autoFocus
              />
            </div>
          )}

          {/* Sections */}
          <div className={cn('max-h-64 overflow-y-auto', ds2026 && 'p-1.5')}>
            {filteredSections.map((section, idx) => (
              <React.Fragment key={section.key}>
                {idx > 0 && (
                  <div
                    className={cn(
                      'border-t',
                      ds2026 ? 'border-neutral-200 my-1.5' : 'border-line'
                    )}
                  />
                )}
                <div
                  className={cn(
                    'flex flex-col',
                    ds2026 ? 'gap-0.5' : 'px-3 py-2 gap-1.5'
                  )}
                >
                  {section.items.map((item) => (
                    <label
                      key={String(item.value)}
                      className={cn(
                        'flex items-center gap-2 cursor-pointer text-sm',
                        ds2026
                          ? 'rounded-lg px-2 py-1.5 text-neutral-700 hover:bg-neutral-100'
                          : 'py-0.5'
                      )}
                    >
                      <Checkbox
                        checked={localSelected.includes(item.value)}
                        onChange={() => toggleValue(item.value)}
                      />
                      <span className="truncate">{item.label}</span>
                    </label>
                  ))}
                </div>
              </React.Fragment>
            ))}
            {filteredSections.every((s) => s.items.length === 0) && (
              <div
                className={cn(
                  'text-sm text-center py-3',
                  ds2026 ? 'text-neutral-500' : 'text-fg-secondary'
                )}
              >
                No items found
              </div>
            )}
          </div>

          {/* Footer */}
          {ds2026 ? (
            // DS2026 table-filter footer: right-aligned Cancel + Apply.
            <div className="border-t border-neutral-200 px-3 py-2 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-8 px-3 rounded-md border border-neutral-300 bg-white text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="h-8 px-3 rounded-md bg-brand-bright text-sm font-medium text-white hover:bg-brand-active transition-colors"
              >
                Apply
              </button>
            </div>
          ) : (
            // Legacy footer: All / None row + full-width Apply.
            <div className="border-t border-line px-3 py-2 flex flex-col gap-2">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="flex-1 h-8 rounded-md border border-line-focus bg-surface-hover text-sm font-medium text-brand-hover hover:bg-surface-hover-deep transition-colors"
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={handleSelectNone}
                  className="flex-1 h-8 rounded-md border border-line bg-surface text-sm font-medium text-fg-secondary hover:bg-surface-hover transition-colors"
                >
                  None
                </button>
              </div>
              <button
                type="button"
                onClick={handleApply}
                className="w-full h-8 rounded-md bg-brand text-base font-medium text-white hover:bg-brand-pressed transition-colors"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
